/**
 * Destinations du chrome v2 (header + footer), partagées entre les pages
 * qui le montent. Déplacées telles quelles depuis HomeV2.jsx.
 */
import { docsFor, LEGAL_SLUGS } from '../../content/legal';

/* ── Routes réelles du site (constatées dans App.js) ─────────────────── */
export const ROUTE_START = '/signup';        // CTA principal — entrée du funnel
export const ROUTE_LOGIN = '/login';         // connexion
export const ROUTE_DASHBOARD = '/dashboard'; // espace connecté
export const ROUTE_PLANS = '/plans';         // tarifs
export const ROUTE_ATTORNEYS = '/attorneys'; // marketplace avocats (page publique)
export const ROUTE_TEAMS = '/entreprises';   // Archer for Teams (offre B2B)

// ── LA nav du site ────────────────────────────────────────────────────
//
// Source unique : la barre, le tiroir mobile et la colonne « Produit » du
// footer lisent toutes ces cinq entrées. Ajouter ou retirer une entrée se
// fait ICI, une fois, et les trois suivent.
//
// Cinq entrées, dans cet ordre — l'ordre visible en production. Il suit
// le parcours mental du prospect, et non l'ordre dans lequel les pages
// ont été écrites :
//
//     le quoi → la confiance → la preuve → le prix → le B2B
//
// « Qui se bat pour toi » remonte donc juste après « Comment ça marche » :
// on répond à « qui êtes-vous » avant de démontrer, et on démontre avant
// d'annoncer un prix. « Tarifs » descend en quatrième pour cette raison —
// ce n'est pas une dévaluation, c'est la place où la question se pose.
// « Archer for Teams » ferme la liste, à part : c'est une autre offre, à
// un autre acheteur, juste avant les boutons d'auth. Pas de pastille
// « nouveau » : même style que les autres.
//
// L'ordre est le même en FR, NL et EN — il n'y a qu'une liste, les
// libellés seuls changent (`NAV_LABELS`).
//
// Tout le reste est délié : Blog (contenu pas prêt), FAQ, « Ce qu'on
// traite », « L'ancien monde vs Archer », Engine en tant qu'entrée
// séparée. Les ROUTES existent toujours et répondent en accès direct —
// elles ne sont simplement plus liées depuis le chrome.
// `testId` est figé sur les valeurs déjà utilisées par le header avant
// cette unification : un sélecteur de test qui marchait doit continuer.
export const NAV_ITEMS = [
  { key: 'howItWorks', to: '/how-it-works', testId: 'how-it-works' },
  { key: 'attorneys',  to: ROUTE_ATTORNEYS, testId: 'attorneys' },
  { key: 'engine',     to: '/engine',       testId: 'engine' },
  { key: 'pricing',    to: ROUTE_PLANS,     testId: 'pricing' },
  { key: 'teams',      to: ROUTE_TEAMS,     testId: 'teams' },
];

// « Archer for Teams » ne se traduit pas : c'est un nom de produit.
export const NAV_LABELS = {
  fr: {
    howItWorks: 'Comment ça marche',
    pricing:    'Tarifs',
    engine:     'Notre IA',
    attorneys:  'Qui se bat pour toi',
    teams:      'Archer for Teams',
  },
  nl: {
    howItWorks: 'Hoe het werkt',
    pricing:    'Prijzen',
    engine:     'Onze AI',
    attorneys:  'Wie voor jou vecht',
    teams:      'Archer for Teams',
  },
  en: {
    howItWorks: 'How it works',
    pricing:    'Pricing',
    engine:     'Our AI',
    attorneys:  'Who fights for you',
    teams:      'Archer for Teams',
  },
};

/** Les cinq entrées, libellées dans la langue demandée. */
export const navFor = (lang) => {
  const labels = NAV_LABELS[String(lang || 'fr').toLowerCase()] || NAV_LABELS.fr;
  return NAV_ITEMS.map((item) => ({ ...item, label: labels[item.key] }));
};

// Compat : les appelants qui n'ont pas de langue sous la main gardent la
// liste française.
export const SITE_NAV = navFor('fr');

export const JURIS_SOON = [
  { flag: '🇫🇷', name: 'France', corpus: 'Code civil · Code du travail' },
  { flag: '🇳🇱', name: 'Pays-Bas', corpus: 'Burgerlijk Wetboek · droit du travail' },
  { flag: '🇩🇪', name: 'Allemagne', corpus: 'BGB · droit du travail' },
];

// ── Footer ────────────────────────────────────────────────────────────
//
// Uniquement des destinations qui EXISTENT, c'est-à-dire de vraies routes
// de App.js. Deux absences assumées, chacune pour une raison précise :
//
//  · « Blog » — contenu pas prêt pour le lancement, délié partout.
//  · icônes réseaux — la maquette les pointait toutes sur "#". Elles
//    reviendront avec de vraies URL de profils, pas avant.
//
// La colonne « Légal » a rejoint le footer avec les quatre pages
// (chantier pages légales) : elle était absente TANT QUE les pages
// n'existaient pas, pour ne pas diffuser quatre liens morts sur toutes
// les pages du site. Elles existent — la colonne aussi.
//
// Les ancres de la home (« #compare », « #cases ») ont quitté la colonne
// Produit : elle est désormais le MIROIR EXACT du menu, cinq entrées,
// mêmes libellés, même ordre — c'est ce qui rend le footer lisible comme
// un rappel de la nav plutôt que comme un second inventaire.
const FOOTER_COPY = {
  fr: {
    product: 'Produit', guides: 'Guides juridiques',
    legal: 'Légal', manageCookies: 'Gérer mes cookies',
  },
  nl: {
    product: 'Product', guides: 'Juridische gidsen',
    legal: 'Juridisch', manageCookies: 'Mijn cookies beheren',
  },
  en: {
    product: 'Product', guides: 'Legal guides',
    legal: 'Legal', manageCookies: 'Manage my cookies',
  },
};

// Colonne « Légal ». Les libellés se traduisent désormais : les quatre
// documents existent en français, en néerlandais et en anglais, et
// l'intitulé promet donc bien la page qui s'ouvrira. Ils viennent du
// registre (`content/legal`) plutôt que d'une liste recopiée ici — un
// libellé de footer qui diverge du titre du document est le genre
// d'écart qu'on ne voit jamais et qui traîne des mois.
const legalLinksFor = (lang) => {
  const docs = docsFor(lang);
  return LEGAL_SLUGS.map((slug) => [docs[slug].label, `/${slug}`]);
};

/**
 * Destination sentinelle du lien « Gérer mes cookies ». Ce n'est pas une
 * route : le footer la reconnaît et efface le choix de consentement au
 * lieu de naviguer (cf. SiteFooterV2).
 */
export const MANAGE_COOKIES_HREF = '#gerer-mes-cookies';

// Les quatre guides pointent vers des pages de contenu FRANÇAIS (pillar
// pages belges). Leurs titres restent en français dans les trois langues :
// traduire l'intitulé d'une page qui s'ouvrira en français promettrait
// une version qui n'existe pas.
const GUIDE_LINKS = [
  ['Contester une amende radar', '/contester-amende-radar'],
  ['Lettre d’huissier : que faire', '/lettre-huissier-que-faire'],
  ['Caution locative non rendue', '/caution-locative-non-rendue'],
  ['Contester une facture abusive', '/facture-abusive-contester'],
  // Le hub : la porte vers TOUS les guides, landings thématiques comprises.
  // Elles n'apparaissaient dans aucun menu, seule porte le sitemap.
  ['Tous les guides', '/guides'],
];

/** Colonnes du footer, dans la langue demandée. */
export const footerColsFor = (lang) => {
  const t = FOOTER_COPY[String(lang || 'fr').toLowerCase()] || FOOTER_COPY.fr;
  return [
    { title: t.product,   links: navFor(lang).map(({ label, to }) => [label, to]) },
    // La colonne « Ressources » a disparu avec sa seule entrée, « Affaires
    // gagnées » : la page a été supprimée. Une colonne vide n'aurait rien
    // annoncé, et lui inventer un second lien pour la garder debout aurait
    // été décorer un trou.
    { title: t.guides,    links: GUIDE_LINKS },
    { title: t.legal,     links: [...legalLinksFor(lang), [t.manageCookies, MANAGE_COOKIES_HREF]] },
  ];
};

// Compat pour un appelant sans langue.
export const FOOTER_COLS = footerColsFor('fr');
